// HeaderOne component - Converted from components/HeaderOne.vue
// Main header component with navigation, mobile menu, and sticky behavior
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMainStore } from '../contexts/StoreContext';
import headerBg from '../assets/images/shapes/header-bg.png';
import logo from '../assets/images/resources/logo.png';

import '../assets/css/hamburger-menu.css';

// Navigation item interface
interface NavItem {
  label: string;
  to?: string;
  children?: NavItem[];
}

const HeaderOne: React.FC = () => {
  const location = useLocation();
  const store = useMainStore();
  const [sticky, setSticky] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Simplified navigation to match new design
  const navItems: NavItem[] = [
    { label: 'Accueil', to: '/', children: undefined },
    { label: 'À propos', to: '/about', children: undefined },
    { label: 'Services', to: '/service', children: undefined },
    { label: 'Projects', to: '/projects', children: undefined },
    { label: 'Contact', to: '/contact', children: undefined },
  ];

  const toggleMenu = useCallback(() => setIsMenuOpen((prev) => !prev), []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  // Handle scroll for sticky header - converted from Vue handleScroll
  useEffect(() => {
    const handleScroll = () => {
      setSticky(window.scrollY > 70);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Toggle search drawer status - converted from Vue changeMobileDrawerStatus
  const changeMobileDrawerStatus = () => {
    store?.toggle?.();
  };

  return (
    <div className="site-header__header-one-wrap">
      <div className="topbar-one">
        <div className="topbar_bg" style={{ backgroundImage: `url(${headerBg})` }}></div>
        <div className="container">
          <div className="topbar-one__left">
            <a href="mailto:needhelp@tripo.com">
              <span className="icon-message"></span>leadersagroelite@gmail.com
            </a>
            <a href="tel:+21627920900">
              <span className="icon-phone-call"></span>27 920 900
            </a>
          </div>
          <div className="topbar-one__middle">
            <Link to="/" className="main-nav__logo">
              <img src={logo} className="main-logo" alt="Awesome Image" style={{ height: '70px', width: 'auto' }} />
            </Link>
          </div>
          <div className="topbar-one__right">
            <div className="topbar-one__social">
              <a
              href="https://www.facebook.com/people/Leaders-Agro-Elite/61585236126030/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-facebook-square"></i>
            </a>
              <a href="https://www.instagram.com/leadersagroelite/" target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
            </div>
          </div>
        </div>
      </div>

      <header className="main-nav__header-one">
        <nav className={`header-navigation stricky clearfix ${sticky ? 'stricked-menu stricky-fixed' : ''}`}>
          <div className="container clearfix">
            <div className="main-nav__left">
              <a 
                href="#" 
                className="main-nav__search search-popup__toggler" 
                onClick={(e) => {
                  e.preventDefault();
                  changeMobileDrawerStatus();
                }}
              >
                <i className="icon-magnifying-glass"></i>
              </a>
            </div>
            <div className="main-nav__main-navigation header-one-nav-desktop">
              <ul className="main-nav__navigation-box">
                {navItems.map((item, index) => (
                  <li
                    key={index}
                    className={`dropdown ${location.pathname === item.to ? 'current' : ''}`}
                  >
                    {item.to ? (
                      <Link to={item.to}>{item.label}</Link>
                    ) : (
                      <a href="#">{item.label}</a>
                    )}
                    {item.children && (
                      <ul>
                        {item.children.map((child, childIndex) => (
                          <li key={childIndex}>
                            {child.children ? (
                              <>
                                <a href="#">{child.label}</a>
                                <ul>
                                  {child.children.map((grandchild, grandIndex) => (
                                    <li key={grandIndex}>
                                      <Link to={grandchild.to || '#'}>{grandchild.label}</Link>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            ) : (
                              <Link to={child.to || '#'}>{child.label}</Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="main-nav__right header-one-mobile-actions">
              <button
                type="button"
                className={`hamburger-btn hamburger-btn-header-one ${isMenuOpen ? 'is-open' : ''}`}
                onClick={toggleMenu}
                aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                aria-expanded={isMenuOpen}
              >
                <span aria-hidden="true" />
                <span aria-hidden="true" />
                <span aria-hidden="true" />
              </button>
            </div>
          </div>
        </nav>

      </header>

      {/* Mobile hamburger menu - same as HomePage */}
      <div
        className={`hamburger-menu-overlay ${isMenuOpen ? 'is-open' : ''}`}
        onClick={toggleMenu}
        onKeyDown={(e) => e.key === 'Escape' && toggleMenu()}
        role="button"
        tabIndex={0}
        aria-label="Fermer le menu"
      />
      <aside
        className={`hamburger-menu-panel ${isMenuOpen ? 'is-open' : ''}`}
        aria-hidden={!isMenuOpen}
      >
        <nav>
          <Link
            to="/"
            className={`nav-link nav-link-logo ${location.pathname === '/' ? 'active' : ''}`}
            onClick={toggleMenu}
          >
            <img src={logo} alt="Agrikol" />
          </Link>
          {navItems.filter((item): item is NavItem & { to: string } => !!item.to).map((navItem) => {
            const isCurrent = location.pathname === navItem.to;
            return (
              <Link
                key={navItem.to}
                to={navItem.to}
                className={`nav-link ${isCurrent ? 'active' : ''}`}
                onClick={toggleMenu}
              >
                {navItem.label}
              </Link>
            );
          })}
        </nav>
        <button
          type="button"
          className="mobile-search-btn"
          onClick={() => {
            changeMobileDrawerStatus();
            toggleMenu();
          }}
        >
          <i className="icon-magnifying-glass" aria-hidden="true" />
          Rechercher
        </button>
      </aside>
    </div>
  );
};

export default HeaderOne;

